import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Play, CalendarPlus, Clock, Trash2, Pencil, PlayCircle, AlertCircle, Info } from 'lucide-react';
import { useIsDemo } from '@/context/DemoContext';
import { useDemoGuard } from '@/components/DemoGuard';

const DURATIONS = [60, 90, 120];

/* ─── Run Practice Bottom Sheet ─── */
function RunPracticeModal({ open, onOpenChange, teams, selectedTeamId, onStart }) {
  const teamId = selectedTeamId || '';
  const selectedTeam = teams.find(t => t.team_id === teamId);
  const [mode, setMode] = useState('auto'); // auto | saved
  const [planId, setPlanId] = useState('');
  const [plans, setPlans] = useState([]);
  const [duration, setDuration] = useState(90);
  const [gameCount, setGameCount] = useState(null);
  const [loadingGames, setLoadingGames] = useState(false);
  const [starting, setStarting] = useState(false);

  // Fetch saved plans for the selected team
  useEffect(() => {
    if (!open || !teamId) { setPlans([]); return; }
    api.get(`/practice-plans?team_id=${teamId}`).then(r => setPlans(r.data.plans || [])).catch(() => {});
  }, [open, teamId]);

  // Check game count for auto-generate availability
  useEffect(() => {
    if (!open || !teamId) { setGameCount(null); return; }
    setLoadingGames(true);
    api.get(`/teams/${teamId}/games`).then(r => {
      const completedGames = (r.data || []).filter(g => g.status === 'final' || g.status === 'locked' || g.status === 'manual_entry');
      setGameCount(completedGames.length);
    }).catch(() => setGameCount(0)).finally(() => setLoadingGames(false));
  }, [open, teamId]);

  // Auto-fallback: if no completed games yet, default to 'saved' on open
  useEffect(() => {
    if (!open) return;
    if (gameCount !== null && gameCount === 0) setMode('saved');
  }, [open, gameCount]);

  const canAutoGen = gameCount !== null && gameCount >= 1;

  const handleStart = async () => {
    if (!teamId) { toast.error('Select a team from the top nav'); return; }
    setStarting(true);
    try {
      const teamName = selectedTeam?.name || '';
      let sections = [];
      let planName = 'Practice';
      let selectedPlanId = null;

      if (mode === 'saved') {
        if (!planId) { toast.error('Select a plan'); setStarting(false); return; }
        const plan = plans.find(p => p.plan_id === planId);
        sections = plan?.sections || [];
        planName = plan?.name || 'Practice';
        selectedPlanId = planId;
      } else {
        const res = await api.post('/practice-plans/generate', {
          team_id: teamId,
          total_duration_minutes: duration,
        });
        sections = res.data.sections;
        planName = `Auto Practice - ${new Date().toLocaleDateString()}`;
      }

      const checklist = [];
      sections.forEach(s => { (s.drills || []).forEach(d => { checklist.push({ drill_name: d.name, section_type: s.section_type, completed: false }); }); });

      const res = await api.post('/scheduled-practices', {
        team_id: teamId,
        team_name: teamName,
        plan_id: selectedPlanId,
        plan_name: planName,
        source: mode === 'saved' ? 'saved' : 'auto',
        sections,
        checklist,
        duration_minutes: mode === 'saved' ? (plans.find(p => p.plan_id === planId)?.total_duration_minutes || 90) : duration,
        status: 'in_progress',
        started_at: new Date().toISOString(),
      });

      onOpenChange(false);
      onStart(res.data.practice_id);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to start practice'));
    } finally { setStarting(false); }
  };

  if (!open) return null;

  /* Bottom-nav height (mobile only). The bottom tab bar in CoachLayout is ~64px tall. */
  const NAV_H = 64;

  return (
    <div
      className="fixed inset-0 z-[300] flex items-end justify-center"
      data-testid="run-practice-bottom-sheet"
      onClick={() => onOpenChange(false)}
      style={{ background: 'rgba(11,24,36,0.6)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md"
        style={{
          background: '#fff',
          borderRadius: '20px 20px 0 0',
          fontFamily: "'Barlow Condensed', sans-serif",
          animation: 'rpSlideUp .22s ease-out',
          maxHeight: '85vh',
          overflowY: 'auto',
          marginBottom: `calc(${NAV_H}px + env(safe-area-inset-bottom))`,
          boxShadow: '0 -10px 30px rgba(0,0,0,.25)',
        }}
      >
        {/* Inject mobile-only bottom margin: hide on md+ where there's no bottom nav */}
        <style>{`
          @keyframes rpSlideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
          @media (min-width: 768px) {
            [data-testid="run-practice-bottom-sheet"] > div { margin-bottom: 0 !important; }
          }
        `}</style>

        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(0,0,0,0.15)' }} />
        </div>

        <div className="px-5 pt-2 pb-6">
          <h2 className="text-2xl font-extrabold uppercase tracking-tight text-slate-900 mb-4" data-testid="run-practice-title">
            Smart Practice
          </h2>

          {!teamId ? (
            <div className="rounded-xl px-4 py-6 text-center" style={{ background: 'rgba(255,161,51,.08)', border: '1.5px solid rgba(255,161,51,.4)' }}>
              <AlertCircle className="w-5 h-5 mx-auto mb-2" style={{ color: '#e8963a' }} />
              <p className="text-[14px] font-semibold text-slate-900">Select a team from the top nav</p>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Mode pills */}
              <div className="grid grid-cols-2 gap-2" data-testid="run-practice-mode-pills">
                <button
                  onClick={() => canAutoGen && setMode('auto')}
                  disabled={!canAutoGen}
                  data-testid="run-mode-auto"
                  className="h-12 rounded-full text-[15px] font-semibold uppercase tracking-[.07em] transition-all active:scale-[.97] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: mode === 'auto' && canAutoGen ? '#0B1824' : '#fff',
                    color: mode === 'auto' && canAutoGen ? '#fff' : '#0B1824',
                    border: `1.5px solid ${mode === 'auto' && canAutoGen ? '#0B1824' : '#DDE5EC'}`,
                    fontFamily: "'Barlow Condensed', sans-serif",
                  }}
                >
                  Auto-Generate
                </button>
                <button
                  onClick={() => setMode('saved')}
                  data-testid="run-mode-saved"
                  className="h-12 rounded-full text-[15px] font-semibold uppercase tracking-[.07em] transition-all active:scale-[.97]"
                  style={{
                    background: mode === 'saved' ? '#0B1824' : '#fff',
                    color: mode === 'saved' ? '#fff' : '#0B1824',
                    border: `1.5px solid ${mode === 'saved' ? '#0B1824' : '#DDE5EC'}`,
                    fontFamily: "'Barlow Condensed', sans-serif",
                  }}
                >
                  Use Saved Plan
                </button>
              </div>

              {/* Helper line */}
              <p className="text-[12px] -mt-3" style={{ color: '#8FA3B1' }}>
                {mode === 'auto'
                  ? (loadingGames ? 'Checking your game history…' : (canAutoGen ? 'Built from your last 3 completed games.' : 'Track at least 1 game to unlock Auto-Generate.'))
                  : `${plans.length} plan${plans.length === 1 ? '' : 's'} available for this team.`}
              </p>

              {/* Saved plan picker */}
              {mode === 'saved' && (
                plans.length === 0 ? (
                  <div className="rounded-xl p-3 text-[13px]" style={{ background: 'rgba(255,161,51,.08)', border: '1.5px solid rgba(255,161,51,.35)', color: '#92743B' }}>
                    No saved plans yet. Tap <strong>Build Practice</strong> on the Practice page to create one.
                  </div>
                ) : (
                  <Select value={planId} onValueChange={setPlanId}>
                    <SelectTrigger className="h-12 rounded-full px-4 text-[15px] font-semibold" data-testid="run-practice-plan-select">
                      <SelectValue placeholder="Select a plan…" />
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map(p => <SelectItem key={p.plan_id} value={p.plan_id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )
              )}

              {/* Duration pills (auto only) */}
              {mode === 'auto' && (
                <div data-testid="run-practice-duration">
                  <p className="text-[11px] font-bold uppercase tracking-[.08em] mb-2" style={{ color: '#8FA3B1' }}>Duration</p>
                  <div className="grid grid-cols-3 gap-2">
                    {DURATIONS.map(d => (
                      <button
                        key={d}
                        onClick={() => setDuration(d)}
                        data-testid={`duration-${d}`}
                        className="h-12 rounded-full text-[15px] font-semibold uppercase tracking-[.07em] transition-all active:scale-[.97]"
                        style={{
                          background: duration === d ? '#0B1824' : '#fff',
                          color: duration === d ? '#fff' : '#0B1824',
                          border: `1.5px solid ${duration === d ? '#0B1824' : '#DDE5EC'}`,
                          fontFamily: "'Barlow Condensed', sans-serif",
                        }}
                      >
                        {d} MIN
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Start Practice — full-width orange pill */}
              <button
                onClick={handleStart}
                disabled={starting || (mode === 'saved' && (!planId || plans.length === 0))}
                data-testid="start-practice-confirm"
                className="w-full h-14 rounded-full text-[15px] font-semibold uppercase tracking-[.07em] flex items-center justify-center gap-2 transition-all active:scale-[.97] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: '#e8963a', color: '#0B1824', border: 'none',
                  fontFamily: "'Barlow Condensed', sans-serif",
                  boxShadow: '0 4px 14px rgba(232,150,58,.35)',
                }}
              >
                <Play className="w-[15px] h-[15px]" />
                {starting ? 'Starting…' : 'Start Practice'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Schedule Practice Modal ─── */
function SchedulePracticeModal({ open, onOpenChange, teams, selectedTeamId, selectedSeasonId, editData, preselectedPlanId, preselectedTeamId, onSaved, onGoToPlans }) {
  const teamId = editData?.team_id || preselectedTeamId || selectedTeamId || '';
  const selectedTeam = teams.find(t => t.team_id === teamId);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(90);
  const [mode, setMode] = useState('existing'); // auto | existing
  const [planId, setPlanId] = useState('');
  const [plans, setPlans] = useState([]);
  const [gameCount, setGameCount] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editData) {
      setDate(editData.scheduled_date || '');
      setTime(editData.scheduled_time || '');
      setDuration(editData.duration_minutes || 90);
      setPlanId(editData.plan_id || '');
      setMode(editData.plan_id ? 'existing' : 'auto');
    } else {
      setDate('');
      setTime('');
      setDuration(90);
      setPlanId(preselectedPlanId || '');
      setMode(preselectedPlanId ? 'existing' : 'existing');
    }
  }, [open, editData, preselectedPlanId]);

  useEffect(() => {
    if (!open || !teamId) { setPlans([]); return; }
    api.get(`/practice-plans?team_id=${teamId}`).then(r => setPlans(r.data.plans || [])).catch(() => {});
    api.get(`/teams/${teamId}/games`).then(r => {
      const completed = (r.data || []).filter(g => g.status === 'final' || g.status === 'locked' || g.status === 'manual_entry');
      setGameCount(completed.length);
    }).catch(() => setGameCount(0));
  }, [open, teamId]);

  const canAutoGen = gameCount !== null && gameCount >= 1;

  const handleSave = async () => {
    if (!teamId) { toast.error('Select a team from the top-right selector'); return; }
    if (!date) { toast.error('Select a date'); return; }
    setSaving(true);
    try {
      const teamName = selectedTeam?.name || '';
      let sections = [];
      let planName = 'Practice';
      let selectedPlanId = null;

      if (mode === 'existing') {
        if (!planId) { toast.error('Select a plan'); setSaving(false); return; }
        const plan = plans.find(p => p.plan_id === planId);
        sections = plan?.sections || [];
        planName = plan?.name || 'Practice';
        selectedPlanId = planId;
      } else if (mode === 'auto') {
        const res = await api.post('/practice-plans/generate', {
          team_id: teamId,
          total_duration_minutes: duration,
        });
        sections = res.data.sections;
        planName = `Auto Practice - ${date}`;
      }

      const checklist = [];
      sections.forEach(s => {
        s.drills.forEach(d => {
          checklist.push({ drill_name: d.name, section_type: s.section_type, completed: false });
        });
      });

      const payload = {
        team_id: teamId,
        team_name: teamName,
        plan_id: selectedPlanId,
        plan_name: planName,
        source: mode === 'auto' ? 'auto' : 'saved',
        sections,
        checklist,
        scheduled_date: date,
        scheduled_time: time,
        duration_minutes: duration,
        status: 'scheduled',
      };

      if (editData?.practice_id) {
        await api.put(`/scheduled-practices/${editData.practice_id}`, payload);
        toast.success('Practice updated');
      } else {
        await api.post('/scheduled-practices', payload);
        toast.success('Practice scheduled');
      }

      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to save'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto z-[300]">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold uppercase tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {editData ? 'Edit Scheduled Practice' : 'Schedule Practice'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Team</label>
            {selectedTeam ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                <span className="text-sm font-semibold text-slate-700" data-testid="schedule-team-display">{selectedTeam.name}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                <span className="text-sm text-amber-700">Select a team from the top-right selector</span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Date</label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-9 text-sm" data-testid="schedule-date" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Start Time</label>
              <Input type="time" value={time} onChange={e => setTime(e.target.value)} className="h-9 text-sm" data-testid="schedule-time" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Duration</label>
            <div className="flex gap-2">
              {DURATIONS.map(d => (
                <button key={d} onClick={() => setDuration(d)}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${duration === d ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                  {d} min
                </button>
              ))}
            </div>
          </div>

          {teamId && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Plan Source</label>
              <div className="space-y-2">
                <button onClick={() => canAutoGen && setMode('auto')} disabled={!canAutoGen}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-colors ${!canAutoGen ? 'opacity-50 cursor-not-allowed border-slate-100' : mode === 'auto' ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid="schedule-mode-auto">
                  <p className="text-sm font-bold">Auto-Generate from Meta Tags</p>
                  {!canAutoGen ? <p className="text-xs text-red-500">Need at least 1 completed game for this team</p>
                   : <p className="text-xs text-slate-500">Uses last 3 rolling games</p>}
                </button>
                <button onClick={() => setMode('existing')}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-colors ${mode === 'existing' ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid="schedule-mode-existing">
                  <p className="text-sm font-bold">Use Saved Plan</p>
                  <p className="text-xs text-slate-500">{plans.length} plan{plans.length !== 1 ? 's' : ''} available</p>
                </button>
              </div>
            </div>
          )}

          {mode === 'existing' && teamId && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Select Plan</label>
              {plans.length === 0 ? (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
                  <p className="font-medium">No saved plans yet</p>
                  <p className="text-xs mt-1">Go to Practice Lab → Practice Plans to create one.</p>
                </div>
              ) : (
                <Select value={planId} onValueChange={setPlanId}>
                  <SelectTrigger className="h-9 text-sm" data-testid="schedule-plan"><SelectValue placeholder="Choose a plan..." /></SelectTrigger>
                  <SelectContent>
                    {plans.map(p => <SelectItem key={p.plan_id} value={p.plan_id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          <Button onClick={handleSave} disabled={saving}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wider text-sm"
            data-testid="schedule-confirm">
            {saving ? 'Saving...' : editData ? 'Update' : 'Schedule Practice'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Main Run Practice Tab ─── */
export default function RunPractice({ user, teams, selectedTeamId, selectedSeasonId, onSwitchTab }) {
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRun, setShowRun] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [editData, setEditData] = useState(null);
  const [preselectedPlanId, setPreselectedPlanId] = useState(null);
  const navigate = useNavigate();
  const isDemo = useIsDemo();
  const demoBlocked = useDemoGuard();

  const fetchPractices = useCallback(async () => {
    try {
      const res = await api.get('/scheduled-practices');
      setPractices(res.data.practices || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPractices(); }, [fetchPractices]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this scheduled practice?')) return;
    try {
      await api.delete(`/scheduled-practices/${id}`);
      toast.success('Deleted');
      fetchPractices();
    } catch { toast.error('Failed to delete'); }
  };

  const handleStart = (practiceId) => {
    navigate(`/coach/practice/run/${practiceId}`);
  };

  const handleEdit = (p) => {
    setEditData(p);
    setShowSchedule(true);
  };

  const statusColor = (s) => {
    if (s === 'completed') return 'bg-emerald-100 text-emerald-700';
    if (s === 'in_progress') return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-600';
  };

  return (
    <div>
      {/* Demo flow removed in single-tier model */}
      <>
      {/* Scheduled Practices List */}
      <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3" data-testid="scheduled-practices-header">
        Scheduled Practices
      </h3>

      {loading ? (
        <p className="text-sm text-slate-400 text-center py-8">Loading...</p>
      ) : practices.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <p className="text-slate-500 font-medium mb-1">No scheduled practices yet</p>
          <p className="text-xs text-slate-400">Run or schedule a practice to get started</p>
        </div>
      ) : (
        <div className="space-y-2">
          {practices.map(p => (
            <div key={p.practice_id} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
              data-testid={`practice-row-${p.practice_id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-900 truncate">{p.plan_name}</p>
                <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500">
                  {p.team_name && <span>{p.team_name}</span>}
                  {p.scheduled_date && <span>{new Date(p.scheduled_date + 'T00:00').toLocaleDateString()}</span>}
                  {p.scheduled_time && <span>{p.scheduled_time}</span>}
                  <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{p.duration_minutes}m</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${statusColor(p.status)}`}>{p.status.replace('_', ' ')}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {p.status !== 'completed' && (
                  <Button size="sm" onClick={() => handleStart(p.practice_id)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase gap-1 h-8"
                    data-testid={`start-practice-${p.practice_id}`}>
                    <PlayCircle className="w-3.5 h-3.5" /> Start
                  </Button>
                )}
                {p.status === 'completed' && (
                  <Button size="sm" variant="outline" onClick={() => handleStart(p.practice_id)}
                    className="text-xs font-bold uppercase gap-1 h-8"
                    data-testid={`view-practice-${p.practice_id}`}>
                    View
                  </Button>
                )}
                {p.status === 'scheduled' && (
                  <Button size="sm" variant="outline" onClick={() => handleEdit(p)}
                    className="text-xs h-8 px-2" data-testid={`edit-practice-${p.practice_id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => handleDelete(p.practice_id)}
                  className="text-xs h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                  data-testid={`delete-practice-${p.practice_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <RunPracticeModal open={showRun} onOpenChange={setShowRun} teams={teams} selectedTeamId={selectedTeamId} selectedSeasonId={selectedSeasonId} onStart={handleStart} />
      <SchedulePracticeModal
        open={showSchedule} onOpenChange={v => { setShowSchedule(v); if (!v) setEditData(null); }}
        teams={teams} selectedTeamId={selectedTeamId} selectedSeasonId={selectedSeasonId}
        editData={editData} preselectedPlanId={preselectedPlanId}
        onSaved={fetchPractices} onGoToPlans={() => onSwitchTab('plans')}
      />
      </>
    </div>
  );
}

// Export modals for use from other components
export { RunPracticeModal, SchedulePracticeModal };
